import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Button,
} from "reactstrap";
import {
  createDocumentFormat,
  updateDocumentFormat,
} from "../../ApiCalls/documentFormats";
import FieldsEditor from "./FieldsEditor";
import "./styles/documentFormatModal.scss";

function DocumentFormatModal({
  isOpen,
  toggle,
  format,
  mantisBusinessId,
  mantisBusinessUserId,
  token,
  onSave,
}) {
  const [formData, setFormData] = useState({
    name: "",
    fields: [],
    suggestedFilename: "",
    suggestedFileLocation: [],
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (format) {
      setFormData({
        name: format.name || "",
        fields: format.fields || [],
        suggestedFilename: format.suggestedFilename || "",
        suggestedFileLocation: format.suggestedFileLocation || [],
      });
    } else {
      resetForm();
    }
    setErrors({});
    setSubmitError("");
  }, [format, isOpen]);

  const resetForm = () => {
    setFormData({
      name: "",
      fields: [],
      suggestedFilename: "",
      suggestedFileLocation: [],
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Format name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFieldsChange = (newFields) => {
    setFormData((prev) => ({
      ...prev,
      fields: newFields,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setSubmitError("");

      if (format) {
        await updateDocumentFormat(token, format._id, formData);
      } else {
        await createDocumentFormat(token, mantisBusinessId, {
          ...formData,
          mantisBusinessUserId,
        });
      }

      onSave();
      toggle();
    } catch (err) {
      setSubmitError(err.message || "Failed to save format");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="document-format-modal">
      <ModalHeader toggle={toggle}>
        {format ? "Edit Document Format" : "Create New Document Format"}
      </ModalHeader>
      <ModalBody>
        {submitError && <Alert color="danger">{submitError}</Alert>}

        <Form>
          {/* Format Name */}
          <FormGroup>
            <Label for="formatName">
              Format Name <span className="required">*</span>
            </Label>
            <Input
              type="text"
              name="name"
              id="formatName"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Invoice, Purchase Order"
              invalid={!!errors.name}
            />
            {errors.name && (
              <small className="text-danger">{errors.name}</small>
            )}
          </FormGroup>

          {/* Suggested Filename */}
          <FormGroup>
            <Label for="suggestedFilename">Suggested Filename</Label>
            <Input
              type="text"
              name="suggestedFilename"
              id="suggestedFilename"
              value={formData.suggestedFilename}
              onChange={handleInputChange}
              placeholder="e.g., invoice_{date}.pdf"
            />
            <small className="text-muted">
              Optional template for file naming
            </small>
          </FormGroup>

          {/* Fields Editor */}
          <div className="fields-section">
            <h6 className="section-title">Document Fields</h6>
            <p className="section-description">
              Define the fields/columns that documents using this format will have
            </p>
            <FieldsEditor
              fields={formData.fields}
              onChange={handleFieldsChange}
            />
          </div>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" /> Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save" /> {format ? "Update" : "Create"} Format
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default DocumentFormatModal;
